import React, { useState } from "react";
import { Modal, Row, Col, Select, Button, Spin } from "antd";
import moment from "moment";
import "moment/locale/es";
import apiParticipantes from "../../../../../api/Participantes/api-participantes";
import apiConvocatorias from "../../../../../api/Convocatorias/api-convocatorias";
// Las funciones que generan los informes arrastran docx (324 KB) y solo se usan al generar uno:
// se cargan en ese momento. Los tipos van aparte porque desaparecen al compilar y no crean
// dependencia en tiempo de ejecucion.
import type {
  PropuestaInforme,
  PropuestaEvaluada,
  GrupoPropuestas,
  GrupoVerificacion,
  SeccionVerificacion,
} from "../../../../../utils/generarInformeWord";
import { ObjNotificaciones } from "../../../../../config/notificaciones";

import { momentCierre } from '../../../../../utils/horaCierre';

interface IProps {
  openModal: boolean;
  setOpenModal: Function;
  lineasConvocatoria: Array<{ id: number; nombre: string }>;
}

// Tipo de informe -> clave de la fecha de la actividad en la convocatoria
const CLAVES_FECHA_INFORME: { [key: string]: string } = {
  inscritos: "Informe de propuestas inscritas",
  primer_verificacion: "Primer informe de verificación de documentos",
  informe_final: "Informe final de verificación de documentos",
  evaluacion: "Informe de evaluación",
};

const OPCIONES_INFORME = [
  { value: "inscritos", label: "Informe de inscritos", disabled: false },
  { value: "primer_verificacion", label: "Primer informe de verificación de documentos", disabled: false },
  { value: "informe_final", label: "Informe final de verificación de documentos", disabled: false },
  { value: "evaluacion", label: "Informe de evaluación", disabled: false },
];

// Estado del sistema -> sección del informe de verificación (con o sin OBSERVACIONES)
const SECCIONES_VERIFICACION = [
  { titulo: "En estudio", estado: "Aceptado", conObservaciones: false },
  { titulo: "Por subsanar", estado: "Subsanar", conObservaciones: true },
  { titulo: "Rechazada", estado: "Rechazado", conObservaciones: true },
];

// Informe final: solo En estudio (Aceptado) y Rechazadas (Rechazado, con observaciones)
const SECCIONES_FINAL = [
  { titulo: "En estudio", estado: "Aceptado", conObservaciones: false },
  { titulo: "Rechazadas", estado: "Rechazado", conObservaciones: true },
];

// Claves de fechas de otros informes (para narrativas de los párrafos 3)
const CLAVE_PRIMER_INFORME = "Primer informe de verificación de documentos";
const CLAVE_FINAL_INFORME = "Informe final de verificación de documentos";

// Clave de la actividad para el plazo de subsanación (párrafos 4 y 5)
const CLAVE_PLAZO_SUBSANAR = "Plazo para subsanar documentos y presentar observaciones";

// Estados que NO se consideran "propuestas recibidas / inscritas"
const ESTADOS_EXCLUIDOS = ["Por completar", "Anulado"];

// Clave de la fecha de publicación de la convocatoria ("publicó el día ...")
const CLAVE_FECHA_PUBLICACION = "Publicación de portafolio";

// Nombre completo del jurado, sin espacios de más por los apellidos vacíos
const nombreJuradoCompleto = (j: any): string =>
  [j.primer_nombre, j.segundo_nombre, j.primer_apellido, j.segundo_apellido]
    .map((x: any) => (x ? String(x).trim() : ""))
    .filter((x: string) => x !== "")
    .join(" ");

// La nota de un jurado es la suma de sus criterios
const mapearJurado = (j: any) => ({
  id: j.id_jurado,
  nombre: nombreJuradoCompleto(j),
  nota: (j.criterios ?? []).reduce((s: number, c: any) => s + (Number(c.nota) || 0), 0),
});

const redondear = (n: number) => (Number.isInteger(n) ? n : Number(n.toFixed(2)));

const notaDeJurado = (juradosData: any[], id: any): number | string => {
  const j = (juradosData ?? []).find((x: any) => x.id === id);
  return j ? redondear(j.nota) : "";
};

export const ModalGenerarInforme: React.FC<IProps> = ({ openModal, setOpenModal, lineasConvocatoria }) => {
  const [idLinea, setIdLinea] = useState<number | undefined>(undefined);
  const [tipoInforme, setTipoInforme] = useState<string | undefined>(undefined);
  const [convocatoria, setConvocatoria] = useState<any>(null);
  const [postulaciones, setPostulaciones] = useState<any[]>([]);
  const [cargandoLinea, setCargandoLinea] = useState(false);
  const [generando, setGenerando] = useState(false);

  const fechas: any[] = convocatoria?.fechas ?? [];

  const buscarFecha = (clave: string) => fechas.find((f) => f.clave === clave);

  const fmtFecha = (valor?: string) =>
    valor ? moment(valor).locale("es").format("D [de] MMMM [de] YYYY") : "";

  const fmtFechaHora = (valor?: string) => {
    if (!valor) return "";
    // Hasta 2026 el cierre se guardaba a las 18:00 para cerrar a las 17:00; la regla lo corrige.
    const m = momentCierre(valor).locale("es");
    const ampm = m.hours() < 12 ? "a. m." : "p. m.";
    return `${m.format("D [de] MMMM [de] YYYY")} a las ${m.format("h:mm")} ${ampm}`;
  };

  // Al seleccionar la línea trae la convocatoria (fechas, objetivo, resolución) y sus postulaciones
  const handleLineaChange = async (id: number) => {
    setIdLinea(id);
    setConvocatoria(null);
    setPostulaciones([]);
    setCargandoLinea(true);
    try {
      const respPost = await apiParticipantes.obtenerPostulacionesByLinea(id);
      const listaPostulaciones: any[] = respPost?.data?.[0] ?? [];
      setPostulaciones(listaPostulaciones);

      const idConvocatoria = listaPostulaciones.find((p) => p.convocatoria_id)?.convocatoria_id;
      if (idConvocatoria) {
        const respConv = await apiConvocatorias.getConvocatoriaById(idConvocatoria);
        setConvocatoria(respConv?.data ?? null);
      }
    } catch (error) {
      console.log(error);
      ObjNotificaciones.MSG_ERROR("error", "No se pudo cargar la línea", "Intenta nuevamente.");
    } finally {
      setCargandoLinea(false);
    }
  };

  // Número de resolución: se busca en los documentos generales de tipo "Resolución"
  const obtenerResolucion = (): string => {
    const docs: any[] = convocatoria?.documentos_generales ?? [];
    const resolucion = docs.find((d) => d?.tipo_documento === "Resolución");
    if (!resolucion) return "";
    // El número puede estar en la descripción o en el nombre del archivo cargado
    for (const texto of [resolucion.descripcion, resolucion.url_documento]) {
      const coincidencias = String(texto ?? "").match(/\d{5,}/g);
      if (coincidencias?.length) {
        return coincidencias.sort((a, b) => b.length - a.length)[0];
      }
    }
    return String(resolucion.descripcion ?? "");
  };

  // Año de la convocatoria/línea (de su nombre o, si no, del año del cierre)
  const obtenerAnioConvocatoria = (): string =>
    (/20\d{2}/.exec(String(convocatoria?.nombre_convocatoria ?? "")) || [])[0] ||
    moment(buscarFecha("Cierre")?.valormax || buscarFecha("Cierre")?.valormin).format("YYYY");

  // Nombre oficial de la convocatoria: "5ª Convocatoria de Fomento y Estímulos para el Arte y la Cultura 2026"
  const construirNombreConvocatoria = (): string => {
    const nombre = String(convocatoria?.nombre_convocatoria ?? "");
    const ordinalMatch = /^\s*(\d+)/.exec(nombre);
    const ordinal = ordinalMatch
      ? ordinalMatch[1]
      : String(convocatoria?.numero_convocatoria ?? "").replace(/\D/g, "");
    return `${ordinal}ª Convocatoria de Fomento y Estímulos para el Arte y la Cultura ${obtenerAnioConvocatoria()}`
      .replace(/\s+/g, " ")
      .trim();
  };

  // Normaliza la categoría de la línea; "" cuando no aplica (línea sin categorías)
  const normalizarCategoria = (cat: any): string => {
    const c = String(cat ?? "").trim();
    return !c || c.toLowerCase() === "no aplica" ? "" : c;
  };

  // Nombre del participante: persona jurídica -> razón social; persona natural / grupo -> nombre completo
  const armarNombreParticipante = (p: any): string => {
    if (String(p.tipo_participante) === "2") {
      // Para PJ se muestra la razón social (no el representante legal)
      return String(
        p.razon_social ||
          p.datos_empresa?.razon_social ||
          p.nombre_participante ||
          p.nombre_representante ||
          ""
      ).trim();
    }
    const partes = [p.primer_nombre, p.segundo_nombre, p.primer_apellido, p.segundo_apellido]
      .map((x: any) => (x == null ? "" : String(x).trim()))
      .filter((x: string) => x !== "");
    return partes.join(" ") || String(p.nombre_participante || p.nombre_representante || "").trim();
  };

  // Ordena por el consecutivo del radicado (C593-002, C593-003, ...)
  const ordenarPorRadicado = (a: any, b: any) => {
    const na = parseInt(String(a.radicado ?? "").split("-").pop() || "0", 10);
    const nb = parseInt(String(b.radicado ?? "").split("-").pop() || "0", 10);
    return na - nb;
  };

  const propuestasRecibidas = (): any[] =>
    postulaciones
      .filter((p) => p.links !== null && !ESTADOS_EXCLUIDOS.includes(p.estado))
      .sort(ordenarPorRadicado);

  const contarEstado = (estado: string): number =>
    postulaciones.filter((p) => p.links !== null && p.estado === estado).length;

  // Arma las secciones por estado (para los informes de verificación), agrupadas por categoría
  const construirSecciones = (
    config: Array<{ titulo: string; estado: string; conObservaciones: boolean }>
  ): SeccionVerificacion[] =>
    config
      .map((s) => {
        const props = postulaciones.filter((p) => p.links !== null && p.estado === s.estado).sort(ordenarPorRadicado);
        const grupos: GrupoVerificacion[] = agruparPorCategoria(props, (p) => ({
          ...mapearPropuesta(p),
          observaciones: s.conObservaciones ? String(p.observaciones ?? "") : undefined,
        }));
        return { titulo: s.titulo, conObservaciones: s.conObservaciones, total: props.length, grupos };
      })
      .filter((s) => s.total > 0);

  // Número de resolución de nombramiento de jurados (documento general "Resolución" cuyo nombre incluye "jurados")
  const obtenerResolucionJurados = (): string => {
    const docs: any[] = convocatoria?.documentos_generales ?? [];
    const resolucion = docs.find(
      (d) =>
        d?.tipo_documento === "Resolución" &&
        /jurad/i.test(`${d?.descripcion ?? ""} ${d?.url_documento ?? ""}`)
    );
    if (!resolucion) return "";
    for (const texto of [resolucion.url_documento, resolucion.descripcion]) {
      const coincidencias = String(texto ?? "").match(/\d{5,}/g);
      if (coincidencias?.length) return coincidencias.sort((a, b) => b.length - a.length)[0];
    }
    return "";
  };

  // Datos del informe de evaluación: notas por jurado, puntaje final y monto (evaluadas y beneficiarios)
  const construirDatosEvaluacion = async (): Promise<{
    enEstudio: number;
    rechazadas: number;
    juradosColumnas: string[];
    evaluadas: PropuestaEvaluada[];
    beneficiarios: PropuestaEvaluada[];
  }> => {
    const resp = await apiParticipantes.obtenerResultadoEvaluacionByLinea(idLinea);
    const resultados: any[] = resp?.data ?? [];
    const evaluadasRaw = resultados.filter((r) => r.estado === "Evaluado" || r.estado === "Beneficiario");
    // Las rechazadas no vienen en los resultados de evaluación (se rechazaron en verificación); se cuentan de las postulaciones
    const rechazadas = contarEstado("Rechazado");

    // Notas por jurado (nota del jurado = suma de sus criterios) y la postulación completa (participante, documento, precio)
    const conJurados = await Promise.all(
      evaluadasRaw.map(async (r) => {
        const [respJ, respP] = await Promise.all([
          apiParticipantes.obtenerJuradosCriterios(r.id_postulacion),
          apiParticipantes.consultarPostulacion(r.id_postulacion),
        ]);
        const jurados = (respJ?.data ?? []).map(mapearJurado);
        // La postulación ya cargada por la línea trae los campos de nombre en el formato correcto
        const postulacionLinea = postulaciones.find((pp) => pp.id_postulacion === r.id_postulacion) || {};
        return { ...r, juradosData: jurados, postulacion: respP?.data ?? {}, postulacionLinea };
      })
    );

    // Jurados distintos (ordenados por nombre) -> columnas "Jurado N {nombre}"
    const mapaJurados = new Map<any, string>();
    conJurados.forEach((r) =>
      r.juradosData.forEach((j: any) => {
        if (!mapaJurados.has(j.id)) mapaJurados.set(j.id, j.nombre);
      })
    );
    const juradosOrden = Array.from(mapaJurados.entries()).sort((a, b) => a[1].localeCompare(b[1], "es"));
    const juradosColumnas = juradosOrden.map(([, nombre], i) => `Jurado ${i + 1} ${nombre}`);

    const fmtMoneda = (valor: any): string => {
      const n = Number(valor);
      return valor == null || valor === "" || isNaN(n) ? "" : `$${n.toLocaleString("es-CO", { maximumFractionDigits: 0 })}`;
    };

    // El participante, documento y precio se toman de la postulación (el resultado de evaluación no los trae)
    const mapear = (r: any): PropuestaEvaluada => {
      const p = r.postulacion || {};
      const pl = r.postulacionLinea || {};
      return {
        radicado: r.radicado ?? pl.radicado ?? p.radicado ?? "",
        nombrePropuesta: r.nombre_propuesta ?? pl.nombre_propuesta ?? p.nombre_propuesta ?? "",
        // El nombre se arma con la postulación de la línea (formato correcto); si no, con la del detalle
        nombreParticipante: armarNombreParticipante(Object.keys(pl).length ? pl : p),
        numeroDocumento: String(pl.numero_documento_participante ?? p.numero_documento_participante ?? r.numero_documento_participante ?? ""),
        notasJurados: juradosOrden.map(([id]) => notaDeJurado(r.juradosData, id)),
        puntajeFinal: r.nota_final != null ? String(r.nota_final) : "",
        monto: fmtMoneda(r.precio ?? p.precio),
      };
    };

    const porPuntaje = (a: PropuestaEvaluada, b: PropuestaEvaluada) =>
      (parseFloat(b.puntajeFinal) || 0) - (parseFloat(a.puntajeFinal) || 0);

    const evaluadas = conJurados.map(mapear).sort(porPuntaje);
    const beneficiarios = conJurados
      .filter((r) => r.estado === "Beneficiario")
      .map(mapear)
      .sort(porPuntaje);

    return { enEstudio: evaluadasRaw.length, rechazadas, juradosColumnas, evaluadas, beneficiarios };
  };

  const fmtHora = (valor?: string): string => {
    if (!valor) return "";
    // Hasta 2026 el cierre se guardaba a las 18:00 para cerrar a las 17:00; la regla lo corrige.
    const m = momentCierre(valor).locale("es");
    return `${m.format("h:mm")} ${m.hours() < 12 ? "a. m." : "p. m."}`;
  };

  const mapearPropuesta = (p: any): PropuestaInforme => ({
    radicado: p.radicado,
    nombrePropuesta: p.nombre_propuesta,
    nombreParticipante: armarNombreParticipante(p),
    numeroDocumento: String(p.numero_documento_participante ?? ""),
  });

  // Agrupa postulaciones por categoría de la línea (ordenadas alfabéticamente)
  const agruparPorCategoria = <T,>(lista: any[], mapItem: (p: any) => T): { categoria: string; propuestas: T[] }[] => {
    const mapa = new Map<string, T[]>();
    lista.forEach((p) => {
      const categoria = normalizarCategoria(p.categoria_linea_convocatoria);
      if (!mapa.has(categoria)) mapa.set(categoria, []);
      mapa.get(categoria).push(mapItem(p));
    });
    return Array.from(mapa.entries())
      .sort((a, b) => a[0].localeCompare(b[0], "es"))
      .map(([categoria, propuestas]) => ({ categoria, propuestas }));
  };

  // Plazo de subsanación (párrafos 4 y 5): "viernes 30 de abril y lunes 4 de mayo hasta las 5:00 p. m."
  const construirPlazoSubsanar = (): string => {
    const actividad = buscarFecha(CLAVE_PLAZO_SUBSANAR);
    if (!actividad) return "";
    const dia = (valor?: string) => (valor ? moment(valor).locale("es").format("dddd D [de] MMMM") : "");
    const inicio = dia(actividad.valormin);
    const fin = dia(actividad.valormax);
    const dias = fin && fin !== inicio ? `${inicio} y ${fin}` : inicio;
    const hora = fmtHora(actividad.valormax || actividad.valormin);
    return hora ? `${dias} hasta las ${hora}` : dias;
  };

  const fechaInformeSeleccionada = (): string => {
    if (!tipoInforme) return "";
    return fmtFecha(buscarFecha(CLAVES_FECHA_INFORME[tipoInforme])?.valormin);
  };

  const cerrar = () => {
    setIdLinea(undefined);
    setTipoInforme(undefined);
    setConvocatoria(null);
    setPostulaciones([]);
    setOpenModal(false);
  };

  const generar = async () => {
    if (!idLinea || !tipoInforme) {
      return ObjNotificaciones.MSG_ERROR("warning", "Datos incompletos", "Selecciona la línea y el tipo de informe.");
    }
    if (!convocatoria) {
      return ObjNotificaciones.MSG_ERROR("warning", "Sin información", "No se encontró la información de la convocatoria para esta línea.");
    }

    const nombreLinea = lineasConvocatoria.find((l) => l.id === idLinea)?.nombre || convocatoria.nombre_convocatoria || "";
    const datosBase = {
      nombreConvocatoria: construirNombreConvocatoria(),
      nombreLinea,
      objetivo: convocatoria.objetivo || "",
      resolucion: obtenerResolucion(),
      fechaPublicacion: fmtFecha(buscarFecha(CLAVE_FECHA_PUBLICACION)?.valormin),
      fechaCierre: fmtFechaHora(buscarFecha("Cierre")?.valormax || buscarFecha("Cierre")?.valormin),
      fechaInforme: fechaInformeSeleccionada(),
    };

    setGenerando(true);
    try {
      if (tipoInforme === "inscritos") {
        const recibidas = propuestasRecibidas();
        if (recibidas.length === 0) {
          ObjNotificaciones.MSG_ERROR("warning", "Sin propuestas", "No hay propuestas inscritas para esta línea.");
          return;
        }
        const grupos: GrupoPropuestas[] = agruparPorCategoria(recibidas, mapearPropuesta);
        await (await import("../../../../../utils/generarInformeWord")).generarInformeInscritos({ ...datosBase, grupos });
      } else if (tipoInforme === "primer_verificacion") {
        const secciones = construirSecciones(SECCIONES_VERIFICACION);
        if (secciones.length === 0) {
          ObjNotificaciones.MSG_ERROR("warning", "Sin propuestas", "No hay propuestas verificadas (aceptadas, por subsanar o rechazadas) para esta línea.");
          return;
        }
        await (await import("../../../../../utils/generarInformeWord")).generarInformeVerificacion({
          ...datosBase,
          totalRecibidas: propuestasRecibidas().length,
          plazoSubsanar: construirPlazoSubsanar(),
          secciones,
        });
      } else if (tipoInforme === "informe_final") {
        // Informe final de verificación de documentos
        const secciones = construirSecciones(SECCIONES_FINAL);
        if (secciones.length === 0) {
          ObjNotificaciones.MSG_ERROR("warning", "Sin propuestas", "No hay propuestas en estudio o rechazadas para esta línea.");
          return;
        }
        await (await import("../../../../../utils/generarInformeWord")).generarInformeFinal({
          ...datosBase,
          totalRecibidas: propuestasRecibidas().length,
          fechaPrimerInforme: fmtFecha(buscarFecha(CLAVE_PRIMER_INFORME)?.valormin),
          primerHabilitadas: contarEstado("Aceptado"),
          primerSubsanar: contarEstado("Subsanado"),
          primerRechazadas: contarEstado("Rechazado"),
          secciones,
        });
      } else {
        // Informe de evaluación
        const evaluacion = await construirDatosEvaluacion();
        if (evaluacion.evaluadas.length === 0) {
          ObjNotificaciones.MSG_ERROR("warning", "Sin evaluaciones", "No hay propuestas evaluadas para esta línea.");
          return;
        }
        await (await import("../../../../../utils/generarInformeWord")).generarInformeEvaluacion({
          ...datosBase,
          resolucionJurados: obtenerResolucionJurados(),
          anio: obtenerAnioConvocatoria(),
          totalRecibidas: propuestasRecibidas().length,
          fechaPrimerInforme: fmtFecha(buscarFecha(CLAVE_PRIMER_INFORME)?.valormin),
          fechaFinalInforme: fmtFecha(buscarFecha(CLAVE_FINAL_INFORME)?.valormin),
          enEstudio: evaluacion.enEstudio,
          rechazadas: evaluacion.rechazadas,
          juradosColumnas: evaluacion.juradosColumnas,
          evaluadas: evaluacion.evaluadas,
          beneficiarios: evaluacion.beneficiarios,
        });
      }
      ObjNotificaciones.MSG_SUCCESS("success", "Informe generado correctamente");
      cerrar();
    } catch (error) {
      console.log(error);
      ObjNotificaciones.MSG_ERROR("error", "Error al generar el informe", "Intenta nuevamente.");
    } finally {
      setGenerando(false);
    }
  };


  // Conteos que acompañan a cada tipo de informe
  const resumenDelInforme = () => {
    if (tipoInforme === "primer_verificacion") {
      return (
        <>
          <div>
            <b>En estudio / aceptadas:</b> {contarEstado("Aceptado")}
          </div>
          <div>
            <b>Subsanar:</b> {contarEstado("Subsanar")}
          </div>
          <div>
            <b>Rechazados:</b> {contarEstado("Rechazado")}
          </div>
        </>
      );
    }
    if (tipoInforme === "informe_final") {
      return (
        <>
          <div>
            <b>En estudio:</b> {contarEstado("Aceptado")}
          </div>
          <div>
            <b>Rechazadas:</b> {contarEstado("Rechazado")}
          </div>
        </>
      );
    }
    if (tipoInforme === "evaluacion") {
      return (
        <>
          <div>
            <b>Evaluadas:</b> {contarEstado("Evaluado") + contarEstado("Beneficiario")}
          </div>
          <div>
            <b>Rechazadas:</b> {contarEstado("Rechazado")}
          </div>
        </>
      );
    }
    return (
      <div>
        <b>Propuestas inscritas:</b> {propuestasRecibidas().length}
      </div>
    );
  };

  return (
    <Modal
      maskStyle={{ backgroundColor: "rgba(31,174,239,0.8)", borderRadius: "6px" }}
      title="Generar informe"
      visible={openModal}
      onCancel={cerrar}
      width={window.innerWidth * 0.5}
      footer={
        <Row justify="end" gutter={[24, 24]}>
          <Col>
            <Button className="button-gray-ghost" onClick={cerrar}>
              Cancelar
            </Button>
          </Col>
          <Col className="justify-content-flex-end">
            <Button className="button-azul-radius" loading={generando} onClick={generar}>
              Generar informe
            </Button>
          </Col>
        </Row>
      }
    >
      <Row gutter={[24, 16]}>
        <Col span={24}>
          <span className="font-size-12px font-family-Montserrat-SemiBold font-color-4B4B4B">Línea</span>
          <Select
            showSearch
            optionFilterProp="children"
            style={{ width: "100%" }}
            placeholder="Seleccionar línea..."
            value={idLinea}
            onChange={(value: number) => handleLineaChange(value)}
          >
            {lineasConvocatoria
              .slice()
              .reverse()
              .map(({ id, nombre }) => (
                <Select.Option key={id} value={id}>
                  {nombre}
                </Select.Option>
              ))}
          </Select>
        </Col>

        <Col span={24}>
          <span className="font-size-12px font-family-Montserrat-SemiBold font-color-4B4B4B">Tipo de informe</span>
          <Select
            style={{ width: "100%" }}
            placeholder="Seleccionar tipo de informe..."
            value={tipoInforme}
            onChange={(value: string) => setTipoInforme(value)}
          >
            {OPCIONES_INFORME.map((opcion) => (
              <Select.Option key={opcion.value} value={opcion.value} disabled={opcion.disabled}>
                {opcion.label}
              </Select.Option>
            ))}
          </Select>
        </Col>

        {cargandoLinea && (
          <Col span={24} className="justify-content-center">
            <Spin /> <span style={{ marginLeft: 8 }}>Cargando información de la convocatoria...</span>
          </Col>
        )}

        {convocatoria && !cargandoLinea && (
          <Col span={24}>
            <div style={{ background: "#F7FBFF", borderRadius: 6, padding: "10px 14px", fontSize: 12 }}>
              <div>
                <b>Publicación:</b> {fmtFecha(buscarFecha(CLAVE_FECHA_PUBLICACION)?.valormin) || "—"}
              </div>
              <div>
                <b>Cierre:</b> {fmtFechaHora(buscarFecha("Cierre")?.valormax || buscarFecha("Cierre")?.valormin) || "—"}
              </div>
              {tipoInforme && (
                <div>
                  <b>Fecha del informe:</b> {fechaInformeSeleccionada() || "—"}
                </div>
              )}
              {resumenDelInforme()}
            </div>
          </Col>
        )}
      </Row>
    </Modal>
  );
};
